import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { getAlluserType } from './userType.service';
import UserTypeCoulmn from './userType.column';
import Loader from '../../../components/loader';
import GenericDataGrid from '../../../components/genericTable';
import { createManagementTableSx } from '../../../styles/tableStyles';
import { managementCreateButtonSx, managementHeaderActionSx, managementHeaderContentSx, managementHeaderSx, managementPageSubtitleSx, managementPageTitleSx } from '../../../styles/managementStyles';
import AlertToast from '../../../components/alertToast';
import InlineAlert from '../../../components/inlineAlert';
import { buttonContentSx } from '../../../styles/dialogStyles';

const dataGridSx = createManagementTableSx();

const HIDDEN_USER_PERSONA_NAMES = new Set(['FARMER', 'AGENT', 'SUPER_ADMIN']);

const isHiddenUserPersona = (item) =>
  HIDDEN_USER_PERSONA_NAMES.has((item?.name || '').toString().trim().toUpperCase());

export default function UserType() {
  const navigate = useNavigate();
  const [userTypeData, setUserTypeData] = useState([]);
  const [errors, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [currenntPagemain, setCurrentPagemain] = useState(0);
  const [rowsperpagemain, setrowsperpagemain] = useState(10);
  const [alert, setAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  // A refused action (e.g. deleting a persona that users are still assigned to) explains what has
  // to be dealt with first. It stays above the table until dismissed rather than being flashed in a
  // toast, which slides away long before it can be acted on.
  const [actionError, setActionError] = useState('');

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const getUserTypeData = useCallback(async (page = currenntPagemain, limit = rowsperpagemain) => {
    setLoading(true);
    const res = await getAlluserType(page, limit);
    if (res) {
      setLoading(false);
      // Deletion is a soft-delete that sets status to DELETED; hide those from the table.
      const rows = Array.isArray(res.data)
        ? res.data.filter(
            (item) =>
              (item?.status || '').toString().toUpperCase() !== 'DELETED' &&
              !isHiddenUserPersona(item),
          )
        : [];
      setUserTypeData(rows);
      setTotalDataCount(rows.length);
    } else {
      setLoading(false);
      setUserTypeData([]);
      setError(res.message || 'Something went wrong');
    }
  }, [currenntPagemain, rowsperpagemain]);

  useEffect(() => {
    getUserTypeData(currenntPagemain, rowsperpagemain);
  }, [currenntPagemain, rowsperpagemain, getUserTypeData]);

  const userTypeCoulmnData = UserTypeCoulmn({
    limit: rowsperpagemain,
    page: currenntPagemain,
    navigate,
    getUserTypeData,
    setAlert,
    setOpenAlert,
    setResponseMsg,
    setLoading,
    setActionError,
  });

  // The /userType endpoint returns the full list (not paginated), while the grid runs in
  // server-pagination mode and renders exactly the rows it is given. Slice to the current page
  // here so paging actually flips through the data instead of always showing the whole list.
  const pagedUserTypeData = userTypeData.slice(
    currenntPagemain * rowsperpagemain,
    currenntPagemain * rowsperpagemain + rowsperpagemain,
  );

  return (
    <>
      <Box sx={managementHeaderSx}>
        <Box sx={managementHeaderContentSx}>
          <Typography className="app-page-title" sx={managementPageTitleSx}>
            User Persona Management
          </Typography>
          <Typography sx={managementPageSubtitleSx}>
            Create and manage user personas used across onboarding and platform access.
          </Typography>
        </Box>
        <Box sx={managementHeaderActionSx}>
          <Button
            variant="contained"
            onClick={() => {
              setLoading(true);
              navigate('/addUserType');
            }}
            sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 260 } }}
          >
            <Box component="span" sx={buttonContentSx}>
              <Icon icon="tabler:plus" width="18" height="18" />
              <span>CREATE USER PERSONA</span>
            </Box>
          </Button>
        </Box>
      </Box>

      <InlineAlert message={actionError} onClose={() => setActionError('')} variant="error" />

      <Box sx={{ width: '100%' }}>
        <GenericDataGrid
          rows={pagedUserTypeData}
          columns={userTypeCoulmnData}
          rowCount={totalDataCount}
          page={currenntPagemain}
          pageSize={rowsperpagemain}
          loading={loading}
          errors={errors}
          checkbox="false"
          dataGridSx={dataGridSx}
          rowHeight={60}
          onPaginationChange={(page, pageSize) => {
            setCurrentPagemain(page);
            setrowsperpagemain(pageSize);
            getUserTypeData(page, pageSize);
          }}
        />
      </Box>

      <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
      <AlertToast open={openAlert && alert === 'false'} onClose={alertClose} message={responseMsg} variant="error" />

      {loading && <Loader />}
    </>
  );
}
